import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { OBJECTIVES, TONES, money } from "@/lib/campaignMeta";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { Sparkles, Check, ArrowLeft, Loader2, Anchor, ImageIcon, Brain } from "lucide-react";
import { toast } from "sonner";

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const recjet = location.state?.recjet || null;
  const recBoatId = recjet?.recommended_boat?.id || "";
  const fromRecjet = params.get("fromRecjet") === "1";
  const categoryParam = params.get("category") || "";
  const [boats, setBoats] = useState([]);
  const [boatId, setBoatId] = useState(params.get("boatId") || recBoatId || "");
  const [objective, setObjective] = useState(params.get("objective") || "general");
  const [tone, setTone] = useState(TONES[0]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [coopCtx, setCoopCtx] = useState(null);
  const [useCoop, setUseCoop] = useState(false);

  useEffect(() => {
    api.get("/boats").then(({ data }) => {
      setBoats(data);
      // RecJet Mind already resolved the exact unit (recommended_boat). Use it as-is.
      // Only fall back to category-based oldest-in-stock selection for a legacy/incomplete
      // recommendation that has no recommended_boat and no explicit boatId. Fully editable.
      if (!params.get("boatId") && !recBoatId && categoryParam) {
        const matches = data.filter((b) => b.category === categoryParam && b.status === "In Stock");
        if (matches.length) {
          matches.sort((a, b) => new Date(a.inventory_date || a.created_at) - new Date(b.inventory_date || b.created_at));
          setBoatId(matches[0].id);
        }
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const boat = boats.find((b) => b.id === boatId);

  useEffect(() => {
    if (boat) setSelectedPhotos(boat.photos || []);
    setUseCoop(false);
    setCoopCtx(null);
    if (boatId) api.get(`/coop/context?boat_id=${boatId}`).then(({ data }) => setCoopCtx(data)).catch(() => setCoopCtx(null));
  }, [boatId]); // eslint-disable-line

  const togglePhoto = (p) =>
    setSelectedPhotos((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const generate = async () => {
    if (!boatId) return toast.error("Select a boat first");
    setGenerating(true);
    try {
      const { data } = await api.post("/campaigns/generate", {
        boat_id: boatId, objective, tone, selected_photos: selectedPhotos,
        ...(fromRecjet && recjet ? { recjet } : {}),
      });
      const coopMeta = coopCtx && coopCtx.digital_coop_available ? {
        oem_support_available: true, oem_support_elected: !!useCoop,
        recjet_oem_id: coopCtx.recjet_oem_id, recjet_brand_id: coopCtx.recjet_brand_id,
        program_id: coopCtx.program_id,
        digital_balance_snapshot: coopCtx.digital_available_amount,
        match_percentage_snapshot: coopCtx.digital_match_percentage,
        promotional_boost_status: coopCtx.promotional_boost_active,
      } : { oem_support_available: false, oem_support_elected: false };
      navigate("/campaign-results", {
        state: { results: data.results, boat: data.boat, config: { boat_id: boatId, objective, tone, selected_photos: selectedPhotos, coop: coopMeta, strategy_source: "manual" } },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate campaign");
    } finally {
      setGenerating(false);
    }
  };
  return (
    <div className="space-y-8 fade-up" data-testid="create-campaign-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-navy">Create Campaign</h1>
        <p className="text-muted-foreground mt-1">Turn a boat into a full marketing campaign in four quick steps.</p>
      </div>

      {fromRecjet && recjet && (
        <div className="rounded-2xl border border-gold/40 bg-gold/5 p-4 flex items-start gap-3" data-testid="recjet-source-notice">
          <div className="w-8 h-8 rounded-lg bg-navy flex items-center justify-center shrink-0"><Brain className="w-4 h-4 text-gold" /></div>
          <div className="text-sm">
            <div className="font-bold text-navy">Built from a RecJet Mind recommendation</div>
            <div className="text-muted-foreground mt-0.5">
              {recjet.category}{recjet.phase_label ? ` (${recjet.phase_label})` : ""}. Creative direction: {recjet.creative_direction}.
              {recjet.suggested_message ? ` Suggested message: "${recjet.suggested_message}"` : ""}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Review and edit everything below before generating.</div>
          </div>
        </div>
      )}

      {/* Step 1 - Boat */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="step-boat">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center">1</span>
          <h2 className="font-serif text-xl font-bold text-navy">Select a Boat</h2>
        </div>
        {boats.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Anchor className="w-10 h-10 mx-auto mb-3 opacity-30" />
            No boats yet. <button onClick={() => navigate("/inventory")} className="text-gold font-semibold">Add one</button> first.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {boats.map((b) => (
              <button key={b.id} onClick={() => setBoatId(b.id)} data-testid={`select-boat-${b.id}`}
                className={`text-left rounded-xl overflow-hidden border-2 transition-all ${boatId === b.id ? "border-gold ring-2 ring-gold/30" : "border-border hover:border-navy/30"}`}>
                <div className="h-28 bg-secondary relative">
                  {b.photos?.[0] ? <img src={resolveImg(b.photos[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Anchor className="w-6 h-6 opacity-30" /></div>}
                  {boatId === b.id && <span className="absolute top-2 right-2 bg-gold text-navy rounded-full p-1"><Check className="w-3 h-3" /></span>}
                </div>
                <div className="p-2.5">
                  <div className="text-[11px] text-muted-foreground">{b.year} · {b.category}</div>
                  <div className="font-semibold text-sm text-navy truncate">{b.manufacturer} {b.model}</div>
                  <div className="text-gold font-mono text-xs font-bold">{money(b.price)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {boat && (
        <>
          {/* Step 2 - Objective */}
          <section className="bg-white rounded-2xl border border-border p-6" data-testid="step-objective">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center">2</span>
              <h2 className="font-serif text-xl font-bold text-navy">Campaign Objective</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {OBJECTIVES.map((o) => {
                const Icon = Icons[o.icon] || Sparkles;
                const active = objective === o.id;
                return (
                  <button key={o.id} onClick={() => setObjective(o.id)} data-testid={`objective-${o.id}`}
                    className={`text-left p-4 rounded-xl border-2 transition-all flex gap-3 ${active ? "border-gold bg-gold/5" : "border-border hover:border-navy/30"}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "gold-gradient text-navy" : "bg-navy/5 text-navy"}`}><Icon className="w-4.5 h-4.5" /></div>
                    <div>
                      <div className="font-semibold text-navy text-sm">{o.label}</div>
                      <div className="text-xs text-muted-foreground leading-snug">{o.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 3 - Photos */}
          <section className="bg-white rounded-2xl border border-border p-6" data-testid="step-photos">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center">3</span>
              <h2 className="font-serif text-xl font-bold text-navy">Choose Photos</h2>
            </div>
            {boat.photos?.length ? (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {boat.photos.map((p, i) => {
                  const sel = selectedPhotos.includes(p);
                  return (
                    <button key={i} onClick={() => togglePhoto(p)} data-testid={`campaign-photo-${i}`}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel ? "border-gold ring-2 ring-gold/30" : "border-border"}`}>
                      <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                      {sel && <span className="absolute top-1.5 right-1.5 bg-gold text-navy rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />No photos on this boat. Add some in Inventory.</div>
            )}
          </section>

          {/* Step 4 - Tone + Generate */}
          <section className="bg-white rounded-2xl border border-border p-6" data-testid="step-generate">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center">4</span>
              <h2 className="font-serif text-xl font-bold text-navy">Tone & Generate</h2>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
              <div className="space-y-1.5 w-full sm:w-64">
                <label className="text-sm font-medium">Campaign Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={generating} className="gold-gradient text-navy font-bold h-14 px-8 text-base gold-sheen flex-1 sm:flex-none" data-testid="generate-campaign-btn">
                {generating ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating with AI…</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate Campaign</>}
              </Button>
            </div>
            {generating && <p className="text-xs text-muted-foreground mt-3">Writing real AI campaign content from this boat's details - this can take a few seconds.</p>}
          </section>

          {coopCtx && coopCtx.digital_coop_available && (
            <section className="bg-white rounded-2xl border-2 border-gold/60 p-6" data-testid="oem-support-panel">
              <h2 className="font-serif text-xl font-bold text-navy mb-1">{coopCtx.oem_name} OEM Marketing Support</h2>
              <p className="text-sm text-muted-foreground mb-4">Digital Advertising co-op available for this model. Funds are reserved through Marine Dealer Connect only when you choose to use them.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
                <div><span className="text-muted-foreground">Digital Available</span><div className="font-bold text-navy">{money(coopCtx.digital_available_amount)}</div></div>
                <div><span className="text-muted-foreground">Standard Match</span><div className="font-bold text-navy">{coopCtx.digital_match_percentage != null ? `${coopCtx.digital_match_percentage}%` : "-"}</div></div>
                {coopCtx.promotional_boost_active && <div><span className="text-muted-foreground">Promotional Boost</span><div className="font-bold text-navy">{coopCtx.boost_match_percentage != null ? `${coopCtx.boost_match_percentage}%` : "Active"}</div></div>}
                <div><span className="text-muted-foreground">Max OEM Support</span><div className="font-bold text-navy">{money(coopCtx.reservable_digital_support_amount ?? coopCtx.digital_max_oem_contribution)}</div></div>
              </div>
              <p className="text-sm font-semibold text-navy mb-4" data-testid="oem-match-summary">
                {coopCtx.oem_name} will match {(coopCtx.promotional_boost_active ? coopCtx.boost_match_percentage : coopCtx.digital_match_percentage) != null ? `${coopCtx.promotional_boost_active ? coopCtx.boost_match_percentage : coopCtx.digital_match_percentage}% of your digital ad spend, ` : ""}up to {money(coopCtx.reservable_digital_support_amount ?? coopCtx.digital_max_oem_contribution)} on this campaign.
              </p>
              <label className="flex items-center gap-2 text-sm font-medium text-navy cursor-pointer" data-testid="use-oem-match-toggle">
                <input type="checkbox" checked={useCoop} onChange={(e) => setUseCoop(e.target.checked)} className="w-4 h-4 accent-gold" />
                Use Available OEM Ad Match
              </label>
            </section>
          )}
        </>
      )}

      <Button variant="ghost" onClick={() => navigate("/inventory")} className="text-muted-foreground" data-testid="back-to-inventory">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Inventory
      </Button>
    </div>
  );
}
